import { IComponentOptions, IController, IScope } from "angular";
import colors from "wwwroot/json/colors.json";
import {
  Aggregation,
  aggregations,
  ChartVisualization,
  chartVisualizations,
  defaultChartSettingsMap,
  IListChartSettings,
  IListProperty,
  ListPropertiesTypes,
  ListService,
  ListTypeChangeEventArgs,
  ListTypes,
  ListView,
} from "../..";
import { GtmhubController } from "@gtmhub/core";
import { ChartSettingsLoader } from "../../services/chart-settings-loader";
import { IModalComponent } from "@gtmhub/core/routing";
import { localize } from "@gtmhub/localization";

export type ChartSettingsResolve = {
  listProperties: IListProperty[];
  listType: ListTypes;
  view: ListView;
  chartSettings: IListChartSettings;
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ChartSettingsComponentCtrl extends IModalComponent<ChartSettingsResolve> {}

export class ChartSettingsComponentCtrl extends GtmhubController implements IController {
  chartCategory: string;
  chartValue: string;
  visualization: ChartVisualization;
  aggregation: Aggregation;
  color: string;
  listProperties: IListProperty[];
  view: ListView;
  colors = colors.general;
  propertiesDictionary: { [fieldName: string]: string };
  visualizationDictionary: { [visualization in ChartVisualization]: string };
  aggregationDictionary: { [aggregation in Aggregation]: string };
  private listType: ListTypes;

  readonly aggregations = aggregations;
  readonly visualizations = chartVisualizations;

  public static $inject = ["$scope", "ChartSettingsLoader", "ListService"];
  constructor(private $scope: IScope, private chartSettingsLoader: ChartSettingsLoader, private listService: ListService) {
    super();
  }

  $onInit(): void {
    this.listProperties = this.resolve.listProperties;
    this.view = this.resolve.view;
    this.listType = this.resolve.listType;
    this.setDictionaries();
    this.setDefaultsOrSelectedValues();
    const chartSettings = this.constructChartSettings();
    this.chartSettingsLoader.resolveChartSettings(chartSettings);
    this.$scope.$on("listTypeChange", (event, args: ListTypeChangeEventArgs) => this.onListTypeChange(args));
    this.$scope.$on("chartSettingsChange", () => this.setChartSettings());
    this.$scope.$on("listViewChange", (event, view: ListView) => this.onListViewChange(view));
  }

  onColumnChange(): void {
    this.onChartSettingsChange();
  }

  onAggregationChange(): void {
    this.onChartSettingsChange();
  }

  toggleAggregation(): void {
    this.aggregation = this.aggregation ? null : "Count";
    this.onAggregationChange();
  }

  onColorChange(c: string): void {
    this.color = c;
    this.onChartSettingsChange();
  }

  onVisualizationChange(): void {
    this.onChartSettingsChange();
  }

  private get defaultSettingsForViewAndType(): IListChartSettings {
    const defaultSettingsForView = defaultChartSettingsMap.get(this.view);
    return defaultSettingsForView.get(this.listType);
  }

  private setDefaultsOrSelectedValues(): void {
    const defaultSettingsForViewAndType = this.defaultSettingsForViewAndType;
    const { color, chartCategory, chartValue, aggregation, visualization } = this.resolve.chartSettings || {}; // persisted settings

    this.color = color || defaultSettingsForViewAndType.color;
    this.chartCategory = chartCategory || defaultSettingsForViewAndType.chartCategory;
    this.chartValue = chartValue || defaultSettingsForViewAndType.chartValue;
    this.aggregation = aggregation !== undefined ? aggregation : defaultSettingsForViewAndType.aggregation;
    this.visualization = visualization || defaultSettingsForViewAndType.visualization;
  }

  private setDefaults(): void {
    const { color, chartCategory, chartValue, aggregation, visualization } = this.defaultSettingsForViewAndType;

    this.color = color;
    this.chartCategory = chartCategory;
    this.chartValue = chartValue;
    this.aggregation = aggregation;
    this.visualization = visualization;
  }

  private setDictionaries(): void {
    this.propertiesDictionary = this.listService.getDictionaryByListType(ListPropertiesTypes[this.listType]);

    this.visualizationDictionary = {
      Pie: localize("pie"),
      Donut: localize("donut"),
    };

    this.aggregationDictionary = {
      Count: localize("row_count"),
      Sum: localize("sum"),
      Average: localize("average"),
      Min: localize("min"),
      Max: localize("max"),
    };
  }

  private onChartSettingsChange(): void {
    const chartSettings = this.constructChartSettings();
    this.chartSettingsLoader.onManualChartSettingsChange(chartSettings);
  }

  private setChartSettings(): void {
    const settings = this.chartSettingsLoader.chartSettings;
    this.chartCategory = settings.chartCategory;
    this.chartValue = settings.chartValue;
    this.aggregation = settings.aggregation;
    this.color = settings.color;
  }

  private onListTypeChange(args: ListTypeChangeEventArgs): void {
    this.listType = args.listType;
    this.listProperties = args.listProperties;
    this.propertiesDictionary = this.listService.getDictionaryByListType(ListPropertiesTypes[args.listType]);
    this.setDefaults();
    const chartSettings = this.constructChartSettings();
    this.chartSettingsLoader.setChartSettings(chartSettings);
  }

  private onListViewChange(view: ListView): void {
    this.view = view;
  }

  private constructChartSettings(): IListChartSettings {
    return {
      chartCategory: this.chartCategory,
      chartValue: this.chartValue,
      aggregation: this.aggregation,
      color: this.color,
      visualization: this.visualization,
    };
  }
}

export const ChartSettingsComponent: IComponentOptions = {
  templateUrl: require("./chart-settings.component.html"),
  controller: ChartSettingsComponentCtrl,
  bindings: {
    resolve: "<",
    close: "&",
    dismiss: "&",
  },
};
